/**
 * React Query Key Factories
 *
 * Hierarchical query keys for all order domains to enable granular cache invalidation
 */

import type { ListQueryParams } from '../api/client';

/**
 * Sales Order query keys
 */
export const salesOrderKeys = {
  all: ['sales-orders'] as const,
  lists: () => [...salesOrderKeys.all, 'list'] as const,
  list: (params?: ListQueryParams) => [...salesOrderKeys.lists(), params] as const,
  byCustomer: (customerId: string, params?: ListQueryParams) =>
    [...salesOrderKeys.all, 'by-customer', customerId, params] as const,
  details: () => [...salesOrderKeys.all, 'detail'] as const,
  detail: (id: string) => [...salesOrderKeys.details(), id] as const,
  lines: (id: string) => [...salesOrderKeys.all, id, 'lines'] as const,
  stockStatus: (id: string) => [...salesOrderKeys.all, id, 'stock-status'] as const,
  linkedShipments: (id: string) => [...salesOrderKeys.all, id, 'linked-shipments'] as const,
  kpis: () => [...salesOrderKeys.all, 'kpis'] as const,
};

/**
 * Purchase Order query keys
 */
export const purchaseOrderKeys = {
  all: ['purchase-orders'] as const,
  lists: () => [...purchaseOrderKeys.all, 'list'] as const,
  list: (params?: ListQueryParams) => [...purchaseOrderKeys.lists(), params] as const,
  details: () => [...purchaseOrderKeys.all, 'detail'] as const,
  detail: (id: string) => [...purchaseOrderKeys.details(), id] as const,
  lines: (id: string) => [...purchaseOrderKeys.all, id, 'lines'] as const,
  kpis: () => [...purchaseOrderKeys.all, 'kpis'] as const,
};

/**
 * Backorder query keys
 */
export const backorderKeys = {
  all: ['backorders'] as const,
  lists: () => [...backorderKeys.all, 'list'] as const,
  list: (params?: ListQueryParams) => [...backorderKeys.lists(), params] as const,
  byOrder: (orderId: string, params?: ListQueryParams) =>
    [...backorderKeys.all, 'by-order', orderId, params] as const,
  byProduct: (productId: string, params?: ListQueryParams) =>
    [...backorderKeys.all, 'by-product', productId, params] as const,
  details: () => [...backorderKeys.all, 'detail'] as const,
  detail: (id: string) => [...backorderKeys.details(), id] as const,
  kpis: () => [...backorderKeys.all, 'kpis'] as const,
};

/**
 * Supplier typeahead search query keys
 */
export const supplierSearchKeys = {
  all: ['supplier-search'] as const,
  search: (query: string) => [...supplierSearchKeys.all, query] as const,
};

/**
 * Customer typeahead search query keys
 */
export const customerSearchKeys = {
  all: ['customer-search'] as const,
  search: (query: string) => [...customerSearchKeys.all, query] as const,
};

/**
 * Warehouse typeahead search query keys
 */
export const warehouseSearchKeys = {
  all: ['warehouse-search'] as const,
  search: (query: string) => [...warehouseSearchKeys.all, query] as const,
};

/**
 * Product typeahead search query keys
 */
export const productSearchKeys = {
  all: ['product-search'] as const,
  search: (query: string) => [...productSearchKeys.all, query] as const,
};

/**
 * Access tags the caller may pick on purchase orders, and their own values
 */
export const accessTagKeys = {
  all: ['orders', 'access-tags'] as const,
  mine: (module: string) => [...accessTagKeys.all, module] as const,
};

/**
 * Company custom fields of an order record type (`purchaseOrder`, `salesOrderLine`, ...)
 */
export const customFieldKeys = {
  all: ['orders', 'custom-fields'] as const,
  mine: (entity: string) => [...customFieldKeys.all, entity] as const,
};
